// Cookie Analytics Provider
//
// This provider handles persistence of analytics context to cookies
// that work across all subdomains. This enables cross-subdomain tracking.
//
// Cookies managed:
// - rose_last_active_session: Last session where user sent a message
// - rose_last_active_session_date: Unix timestamp (milliseconds) when last session was active
// - rose_client_id: Stable client ID for traffic control and tracking
//
// Cookie details:
// - Scope: All subdomains (domain=.example.com)
// - Expiry: 1 year
// - Secure: true (HTTPS only)
// - SameSite: Lax (balance between security and functionality)

import { logger } from '../../utils/core/logger';
import { maskId } from '../../utils/core/mask';
import type { AnalyticsContext, AnalyticsEvent } from '../types';
import { BaseAnalyticsProvider } from './base';

export class CookieProvider extends BaseAnalyticsProvider {
    public readonly name = 'cookie';
    private readonly sessionCookieName = 'rose_last_active_session';
    private readonly sessionDateCookieName = 'rose_last_active_session_date';
    private readonly clientIdCookieName = 'rose_client_id';
    private readonly cookieMaxAge = 365 * 24 * 60 * 60; // 1 year in seconds

    /**
     * Cookie provider doesn't track individual events,
     * only context updates via updateContext()
     */
    public eventFilter(_event: AnalyticsEvent): boolean {
        return false;
    }

    /**
     * Persist analytics context to cookies
     * This is called whenever the global analytics context changes
     */
    public override updateContext(context: Partial<AnalyticsContext>): void {
        // Call base implementation to update internal context
        super.updateContext(context);

        // Persist lastActiveSessionId and its timestamp
        if (context.lastActiveSessionId !== undefined) {
            if (context.lastActiveSessionId) {
                // Use the timestamp from context if provided, otherwise generate one
                const timestamp = context.lastActiveSessionDate ?? Date.now();
                this.setCookie(this.sessionCookieName, context.lastActiveSessionId);
                this.setCookie(this.sessionDateCookieName, timestamp.toString());
                logger.debug(
                    `🍪 Cookie: Stored lastActiveSessionId: ${maskId(context.lastActiveSessionId)} at ${new Date(timestamp).toISOString()}`
                );
            } else {
                // If lastActiveSessionId is explicitly set to null/empty, clear both cookies
                this.deleteCookie(this.sessionCookieName);
                this.deleteCookie(this.sessionDateCookieName);
                logger.debug('🍪 Cookie: Cleared lastActiveSessionId and date');
            }
        } else if (context.lastActiveSessionDate !== undefined) {
            // If only the date is updated, update the cookie
            if (context.lastActiveSessionDate) {
                this.setCookie(
                    this.sessionDateCookieName,
                    context.lastActiveSessionDate.toString()
                );
                logger.debug(
                    `🍪 Cookie: Updated lastActiveSessionDate to ${new Date(context.lastActiveSessionDate).toISOString()}`
                );
            }
        }

        // Persist clientId ONLY when lastActiveSessionId exists
        // This prevents overwriting the clientId from domain1 when PostHog initializes on domain2
        // without an active session. The clientId should only be stored when the user has
        // an active conversation (indicated by lastActiveSessionId).
        if (context.clientId !== undefined) {
            // Get the current lastActiveSessionId from the merged context
            const currentLastActiveSessionId = this.getContext().lastActiveSessionId;

            if (context.clientId && currentLastActiveSessionId) {
                // Only store clientId if there's an active session
                this.setCookie(this.clientIdCookieName, context.clientId);
                logger.debug(
                    `🍪 Cookie: Stored clientId: ${maskId(context.clientId)} (session active: ${maskId(currentLastActiveSessionId)})`
                );
            } else if (!context.clientId) {
                // If clientId is explicitly set to null/empty, clear the cookie
                this.deleteCookie(this.clientIdCookieName);
                logger.debug('🍪 Cookie: Cleared clientId');
            } else {
                // clientId exists but no active session - don't store to cookie
                logger.debug(
                    `🍪 Cookie: Skipped storing clientId (no active session). ClientId: ${maskId(context.clientId)}`
                );
            }
        }
    }

    /**
     * Set cookie with a value
     * Cookie is set for the root domain to work across all subdomains
     */
    private setCookie(name: string, value: string): void {
        try {
            const domain = this.getRootDomain();
            const secure = window.location.protocol === 'https:';

            // Build cookie string with all security attributes
            const cookieValue = [
                `${name}=${encodeURIComponent(value)}`,
                `max-age=${this.cookieMaxAge}`,
                `path=/`,
                domain ? `domain=${domain}` : '',
                secure ? 'Secure' : '',
                'SameSite=Lax',
            ]
                .filter(Boolean)
                .join('; ');

            // biome-ignore lint/suspicious/noDocumentCookie: CookieProvider intentionally manages browser cookies.
            document.cookie = cookieValue;

            logger.debug('🍪 Cookie set:', {
                name,
                value: maskId(value),
                domain,
                secure,
                maxAge: this.cookieMaxAge,
            });
        } catch (error) {
            logger.error('Failed to set cookie:', error);
        }
    }

    /**
     * Delete a cookie by name
     */
    private deleteCookie(name: string): void {
        try {
            const domain = this.getRootDomain();

            // Set cookie with past expiry date to delete it
            const cookieValue = [
                `${name}=`,
                'max-age=0',
                'path=/',
                domain ? `domain=${domain}` : '',
            ]
                .filter(Boolean)
                .join('; ');

            // biome-ignore lint/suspicious/noDocumentCookie: CookieProvider intentionally manages browser cookies.
            document.cookie = cookieValue;

            logger.debug('🍪 Cookie deleted:', {
                name,
                domain,
            });
        } catch (error) {
            logger.error('Failed to delete cookie:', error);
        }
    }

    /**
     * Get the root domain for setting cookies that work across subdomains
     * Uses a probe-based approach to find the widest valid domain.
     *
     * Examples:
     * - www.example.com → .example.com
     * - subdomain.example.co.uk → .example.co.uk (not .co.uk - public suffix)
     * - subdomain.example.com → .example.com
     * - localhost → null (no domain attribute for localhost)
     * - 192.168.1.1 → null (no domain attribute for IPs)
     *
     * The probe approach tries candidate domains from widest to narrowest
     * and returns the first one that successfully accepts a test cookie.
     * This avoids issues with public suffixes like .co.uk, .com.au, .github.io.
     */
    private getRootDomain(): string | null {
        try {
            const hostname = window.location.hostname;

            // Don't set domain for localhost or IP addresses
            if (
                hostname === 'localhost' ||
                hostname === '127.0.0.1' ||
                /^\d+\.\d+\.\d+\.\d+$/.test(hostname) // IP address pattern
            ) {
                return null;
            }

            // Split hostname into parts
            const parts = hostname.split('.');

            // If only one part (like "localhost"), return null
            if (parts.length < 2) {
                return null;
            }

            // Generate candidate domains from widest to narrowest
            // For www.example.co.uk: ['.co.uk', '.example.co.uk', '.www.example.co.uk']
            const candidates: string[] = [];
            for (let i = parts.length - 1; i >= 1; i--) {
                candidates.push(`.${parts.slice(i - 1).join('.')}`);
            }

            // Try each candidate by setting a test cookie
            const testCookieName = '__rose_domain_test__';
            const testValue = 'test';

            for (const candidate of candidates) {
                // Try to set a test cookie with this domain
                const testCookie = [
                    `${testCookieName}=${testValue}`,
                    `domain=${candidate}`,
                    'path=/',
                    'max-age=1', // Expire in 1 second
                ].join('; ');

                // biome-ignore lint/suspicious/noDocumentCookie: CookieProvider intentionally probes writable cookie domains.
                document.cookie = testCookie;

                // Check if the cookie was successfully set
                const cookies = document.cookie.split(';');
                const wasSet = cookies.some((cookie) => {
                    const [name] = cookie.trim().split('=');
                    return name === testCookieName;
                });

                // Clean up test cookie immediately
                // biome-ignore lint/suspicious/noDocumentCookie: CookieProvider intentionally cleans up its domain probe cookie.
                document.cookie = `${testCookieName}=; domain=${candidate}; path=/; max-age=0`;

                if (wasSet) {
                    logger.debug(`🍪 Found valid root domain: ${candidate}`);
                    return candidate;
                }
            }

            // If no candidate worked, return null (use current hostname only)
            logger.warn('No valid root domain found, cookies will be hostname-only');
            return null;
        } catch (error) {
            logger.warn('Failed to determine root domain:', error);
            return null;
        }
    }

    /**
     * Get a cookie value by name
     */
    private getCookieValue(cookieName: string): string | null {
        try {
            const cookies = document.cookie.split(';');
            for (const cookie of cookies) {
                const [name, value] = cookie.trim().split('=');
                if (name === cookieName) {
                    return decodeURIComponent(value);
                }
            }
            return null;
        } catch (error) {
            logger.error('Failed to read cookie:', error);
            return null;
        }
    }

    /**
     * Get the last active session ID from cookie (useful for debugging and external access)
     */
    public getLastActiveSessionId(): string | null {
        return this.getCookieValue(this.sessionCookieName);
    }

    /**
     * Get the last active session date from cookie (useful for debugging and external access)
     */
    public getLastActiveSessionDate(): number | null {
        const dateStr = this.getCookieValue(this.sessionDateCookieName);
        return dateStr ? parseInt(dateStr, 10) : null;
    }

    /**
     * Get the client ID from cookie (useful for debugging and external access)
     */
    public getClientId(): string | null {
        return this.getCookieValue(this.clientIdCookieName);
    }

    /**
     * Get all Rose cookies (useful for debugging)
     */
    public getAllCookies(): {
        lastActiveSessionId: string | null;
        lastActiveSessionDate: number | null;
        clientId: string | null;
    } {
        return {
            lastActiveSessionId: this.getLastActiveSessionId(),
            lastActiveSessionDate: this.getLastActiveSessionDate(),
            clientId: this.getClientId(),
        };
    }

    /**
     * Cookie doesn't track individual events
     */
    protected async sendEvent(_event: AnalyticsEvent): Promise<void> {
        // No-op: Cookie only persists context, not individual events
    }
}
